import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderButton,
  HeaderSidebarTrigger,
} from '@/app/ui/components/header';
import { EvaluationTypeLabelsEnum, SummarizationNamespaces } from '@/app/utils/constants';
import { SearchFocusIcon, FileDownloadIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import useExportReport from '@/app/components/shared/ExportButton/useExportReport';
import { ResourceConfig } from '@/app/components/features/evaluations/ResourceView/config';
import { SummaryPageContent } from '@/app/components/features/evaluations/Summary/SummaryPageContent';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { useSuspenseQuery } from '@tanstack/react-query';
import { validator } from '@/app/validator';
import z from 'zod';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/_layout/(main)/summary',
)({
  component: RouteComponent,
  validateSearch: validator(
    z.object({
      targetStatementId: z.string().optional(),
    }),
  ),
});

function RouteComponent() {
  const tGeneral = useTranslations('general');
  const t = useTranslations('pages.tenderHome');
  const tFeature = useTranslations('features.summary');
  const navigate = useNavigate();
  const config = ResourceConfig['tenderPackage'];

  const { tenderPackageId, projectId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));
  const { exportMutation } = useExportReport(config.reportType, tenderPackageId);

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <HugeiconsIcon icon={SearchFocusIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbPage>{tFeature('title')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderButton
          aria-label={tGeneral('generateReport')}
          pending={exportMutation.isPending}
          onClick={() => exportMutation.mutate(tenderPackageId)}
        >
          <HugeiconsIcon icon={FileDownloadIcon} />
        </HeaderButton>
        <HeaderSidechatButton />
      </Header>
      <SummaryPageContent
        evaluationId={tenderPackageId}
        summaryType={SummarizationNamespaces.TENDER_PACKAGE}
        reportType={config.reportType}
      />
    </>
  );
}
